import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  EmailAuthProvider,
  RecaptchaVerifier,
  linkWithCredential,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPhoneNumber,
  signOut,
} from 'firebase/auth';
import toast from 'react-hot-toast';
import { auth } from '../config/firebase';
import { authAPI } from '../utils/api';

/**
 * Authentification Firebase, partagée avec les applications mobiles.
 * Un compte créé ici est utilisable dans l'appli, et inversement.
 *
 * Deux notions distinctes, comme côté Dart :
 *   - le compte Firebase Auth (`firebaseUser`)
 *   - le profil métier, document `Utilisateur` (`user`)
 * Le second peut manquer alors que le premier existe : c'est l'état
 * intermédiaire du parcours d'inscription, signalé par `needsProfile`.
 */
const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

/** Traduit les codes d'erreur Firebase en messages lisibles. */
const authErrorMessage = (error) => {
  switch (error?.code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'Email ou mot de passe incorrect';
    case 'auth/invalid-email':
      return 'Adresse email invalide';
    case 'auth/user-disabled':
      return 'Ce compte a été désactivé';
    case 'auth/too-many-requests':
      return 'Trop de tentatives. Réessayez plus tard.';
    case 'auth/email-already-in-use':
      return 'Cet email est déjà utilisé';
    case 'auth/weak-password':
      return 'Mot de passe trop faible (6 caractères minimum)';
    case 'auth/invalid-phone-number':
      return 'Numéro de téléphone invalide';
    case 'auth/invalid-verification-code':
      return 'Code incorrect. Veuillez réessayer.';
    case 'auth/code-expired':
      return 'Code expiré. Demandez-en un nouveau.';
    case 'auth/quota-exceeded':
      return 'Quota SMS dépassé. Réessayez plus tard.';
    case 'auth/network-request-failed':
      return 'Connexion impossible. Vérifiez votre réseau.';
    default:
      return error?.message || 'Une erreur est survenue';
  }
};

export const AuthProvider = ({ children }) => {
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [user, setUser] = useState(null);
  const [needsProfile, setNeedsProfile] = useState(false);
  const [loading, setLoading] = useState(true);

  /** Recharge le profil métier depuis l'API. */
  const refreshProfile = useCallback(async () => {
    try {
      const { data } = await authAPI.getMe();
      if (data?.needsProfile) {
        setUser(null);
        setNeedsProfile(true);
        return null;
      }
      setUser(data);
      setNeedsProfile(false);
      return data;
    } catch (error) {
      setUser(null);
      setNeedsProfile(false);
      return null;
    }
  }, []);

  // Le SDK restaure la session tout seul au chargement : c'est lui qui fait foi
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (account) => {
      setFirebaseUser(account);

      if (account) {
        await refreshProfile();
      } else {
        setUser(null);
        setNeedsProfile(false);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, [refreshProfile]);

  /* ── Connexion ─────────────────────────────────────────────── */

  const login = useCallback(async (email, password) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
      const profile = await refreshProfile();
      toast.success('Connexion réussie !');
      return { success: true, profile };
    } catch (error) {
      toast.error(authErrorMessage(error));
      return { success: false };
    }
  }, [refreshProfile]);

  const logout = useCallback(async () => {
    await signOut(auth);
    toast.success('Déconnexion réussie');
  }, []);

  const resetPassword = useCallback(async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      toast.success('Email de réinitialisation envoyé');
      return true;
    } catch (error) {
      toast.error(authErrorMessage(error));
      return false;
    }
  }, []);

  /* ── Parcours OTP ──────────────────────────────────────────── */

  /** Ce numéro a-t-il déjà un compte ? */
  const checkPhone = useCallback(async (phone) => {
    const { data } = await authAPI.checkPhone(phone);
    return data.exists;
  }, []);

  /**
   * Canal SMS — Firebase Phone Auth, de bout en bout dans le navigateur,
   * comme dans l'appli. Le reCAPTCHA invisible est exigé par Firebase sur le
   * web ; il n'a pas d'équivalent mobile, où l'attestation d'app le remplace.
   */
  const sendSmsOtp = useCallback(async (phone, containerId) => {
    try {
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = null;
      }

      window.recaptchaVerifier = new RecaptchaVerifier(auth, containerId, { size: 'invisible' });

      const confirmation = await signInWithPhoneNumber(auth, phone, window.recaptchaVerifier);
      window.confirmationResult = confirmation;

      return { success: true };
    } catch (error) {
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = null;
      }
      return { success: false, message: authErrorMessage(error) };
    }
  }, []);

  const confirmSmsOtp = useCallback(async (code) => {
    try {
      if (!window.confirmationResult) {
        return { success: false, message: 'Session expirée. Redemandez un code.' };
      }

      await window.confirmationResult.confirm(code);
      window.confirmationResult = null;

      const profile = await refreshProfile();
      return { success: true, existing: Boolean(profile), profile };
    } catch (error) {
      return { success: false, message: authErrorMessage(error) };
    }
  }, [refreshProfile]);

  /** Canal WhatsApp — VerifyWay, entièrement côté serveur. */
  const sendWhatsappOtp = useCallback(async (phone) => {
    try {
      const { data } = await authAPI.sendWhatsappOtp(phone);
      return { success: true, isNewUser: data.isNewUser };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Envoi impossible',
        retryAfter: error.response?.data?.retryAfter,
      };
    }
  }, []);

  /**
   * Le serveur vérifie le code, puis :
   *   - numéro connu    → renvoie l'email, pour pré-remplir la connexion
   *   - nouveau numéro  → renvoie un jeton personnalisé qui ouvre la session
   */
  const verifyWhatsappOtp = useCallback(async (phone, code) => {
    try {
      const { data } = await authAPI.verifyWhatsappOtp(phone, code);

      if (data.existing) {
        return { success: true, existing: true, email: data.email };
      }

      await signInWithCustomToken(auth, data.customToken);
      return { success: true, existing: false };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || authErrorMessage(error),
      };
    }
  }, []);

  /**
   * Dernière étape : lie un email et un mot de passe au compte ouvert par
   * OTP, puis crée le profil métier. Équivalent web de `linkEmailPassword`
   * suivi de `saveUserInfo` côté Dart.
   */
  const completeSignup = useCallback(async ({ nom, genre, email, password, telephone }) => {
    try {
      const account = auth.currentUser;
      if (!account) {
        return { success: false, message: 'Session expirée. Reprenez l\'inscription.' };
      }

      // Un compte ouvert par OTP n'a pas encore d'email : on le lui attache.
      // S'il en a déjà un (reprise après interruption), on passe l'étape.
      const alreadyLinked = account.providerData.some((p) => p.providerId === 'password');
      if (!alreadyLinked) {
        await linkWithCredential(account, EmailAuthProvider.credential(email, password));
      }

      await authAPI.createProfile({ nom, genre, telephone });
      const profile = await refreshProfile();

      toast.success('Compte créé avec succès !');
      return { success: true, profile };
    } catch (error) {
      const message = error.response?.data?.message || authErrorMessage(error);
      toast.error(message);
      return { success: false, message };
    }
  }, [refreshProfile]);

  const value = useMemo(() => ({
    // État
    user,
    firebaseUser,
    loading,
    needsProfile,
    isAuthenticated: Boolean(user),
    isSmart: ['smart', 'livreur'].includes(user?.role),

    // Session
    login,
    logout,
    resetPassword,
    refreshProfile,

    // Inscription par OTP
    checkPhone,
    sendSmsOtp,
    confirmSmsOtp,
    sendWhatsappOtp,
    verifyWhatsappOtp,
    completeSignup,
  }), [
    user, firebaseUser, loading, needsProfile,
    login, logout, resetPassword, refreshProfile,
    checkPhone, sendSmsOtp, confirmSmsOtp, sendWhatsappOtp, verifyWhatsappOtp, completeSignup,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
